import { useOffline } from '../OfflineProvider';
import { offlineCapability, CAPABILITY_MESSAGES } from '../utils/offline/capabilities';

/**
 * Turn a last-updated timestamp into something a person can read at a glance.
 * Deliberately coarse: the useful question is "is this stale?", not the minute.
 */
export const describeLastSynced = (isoString, now = Date.now()) => {
  if (!isoString) return 'not yet updated';
  const then = Date.parse(isoString);
  if (Number.isNaN(then)) return 'not yet updated';

  const minutes = Math.floor((now - then) / 60000);
  if (minutes < 1) return 'up to date';
  if (minutes < 60) return `updated ${minutes} minute${minutes === 1 ? '' : 's'} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `updated ${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.floor(hours / 24);
  return `updated ${days} day${days === 1 ? '' : 's'} ago`;
};

/**
 * Tells the user when they are offline and how stale what they are looking at
 * is (FR-006, FR-016).
 *
 * Deliberately not a blocking or dismissible state: offline is a normal way to
 * use this app, and navigation must keep working (FR-006). It renders nothing
 * at all when online with nothing pending, so the common case costs no space.
 */
const OfflineBanner = () => {
  const { online, lastUpdatedAt, pendingCount } = useOffline();

  // A page that cannot support offline at all should say so, rather than
  // letting downloads and reloads fail for reasons the user cannot see. In
  // production this never renders — the app is served over HTTPS.
  const { capable, reason } = offlineCapability();
  if (!capable) {
    return (
      <div role="alert" className="bg-yellow-500 px-4 py-2 text-sm text-black">
        {CAPABILITY_MESSAGES[reason] || CAPABILITY_MESSAGES.unsupported}
      </div>
    );
  }

  if (online && pendingCount === 0) return null;

  if (!online) {
    return (
      <div role="status" className="bg-gray-700 text-white px-4 py-2 text-sm flex items-center gap-2">
        <span aria-hidden="true">●</span>
        <span>
          You&apos;re offline — showing what&apos;s saved on this device ({describeLastSynced(lastUpdatedAt)}).
          {pendingCount > 0 && ` ${pendingCount} change${pendingCount === 1 ? '' : 's'} will sync when you reconnect.`}
        </span>
      </div>
    );
  }

  return (
    <div role="status" className="bg-gray-700 text-white px-4 py-2 text-sm">
      Syncing {pendingCount} change{pendingCount === 1 ? '' : 's'}…
    </div>
  );
};

export default OfflineBanner;
